import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, PRIORITIES } from "@/lib/constants";
import { href } from "@/lib/tasks";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", client_id: "", category: "Accounts", duration_minutes: 60, priority: "medium", deadline: "", start_date: "", notes: "", user_id: "" };
const PRESETS = [30, 60, 90, 120, 180, 240];
const field = "rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11";

function PickSelect({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={field} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Subtasks({ task }) {
  const [items, setItems] = useState(task.subtasks);
  const toggle = async (i) => {
    const next = items.map((x, j) => (j === i ? { ...x, done: !x.done } : x));
    setItems(next);
    await api.patch(`/tasks/${task.id}`, { subtasks: next }).catch((e) => toast.error(formatApiError(e)));
  };
  return (
    <div className="space-y-1.5" data-testid="task-subtasks">
      <Label>Subtasks</Label>
      {items.map((x, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-[#0F172A]/75">
          <input type="checkbox" checked={x.done} onChange={() => toggle(i)} className="accent-[#103080]" data-testid={`task-subtask-${i}`} />
          <span className={`flex-1 ${x.done ? "line-through opacity-50" : ""}`}>{x.title}</span>
          {x.sop_link && <a href={href(x.sop_link)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#0090D0] hover:underline">SOP</a>}
          {x.template_link && <a href={href(x.template_link)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#0090D0] hover:underline">Template</a>}
        </div>
      ))}
    </div>
  );
}

function ClientPick({ value, onChange, disabled }) {
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)} disabled={disabled}>
      <SelectTrigger className={field} data-testid="task-client-select"><SelectValue placeholder="No client" /></SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="none">No client</SelectItem>
        {clients.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`task-client-option-${c.id}`}>{c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function TaskDialog({ task, onClose, onSaved, team, defaultUserId }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(task?.id);

  useEffect(() => {
    if (task) setForm({ ...EMPTY, user_id: defaultUserId || "", ...task, client_id: task.client_id || "", deadline: task.deadline || "", start_date: task.start_date || "" });
  }, [task, defaultUserId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      title: form.title, category: form.category, priority: form.priority,
      duration_minutes: Number(form.duration_minutes), deadline: form.deadline || null, start_date: form.start_date || null, notes: form.notes,
      ...(!editing || (!task.card_id && form.client_id !== (task.client_id || "")) ? { client_id: form.client_id || null } : {}),
      ...(form.user_id ? { user_id: form.user_id } : {}),
    };
    try {
      if (editing) await api.patch(`/tasks/${task.id}`, body);
      else await api.post("/tasks", body);
      toast.success(editing ? "Task updated" : "Task added and scheduled");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const unpin = async () => {
    try {
      await api.patch(`/tasks/${task.id}`, { pinned: [] });
      toast.success("Unpinned. Auto-planning can move it again.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={Boolean(task)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription>Tell us how long it takes and when it's due. We'll find the time.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Task</Label>
            <Input required value={form.title} onChange={set("title")} className={field} placeholder="e.g. Q3 VAT return" data-testid="task-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Client</Label>
              {task?.card_id ? <Input disabled value={form.client_name} className={field} data-testid="task-client-input" />
                : <ClientPick value={form.client_id} onChange={set("client_id")} />}
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <PickSelect value={form.category} onChange={set("category")} testId="task-category-select"
                options={Object.keys(CATEGORIES).map((c) => ({ value: c, label: c }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>How long will it take? (minutes)</Label>
            <div className="flex flex-wrap gap-2">
              <Input type="number" min={5} step={5} required value={form.duration_minutes} onChange={set("duration_minutes")}
                className={`${field} w-28`} data-testid="task-duration-input" />
              {PRESETS.map((m) => (
                <button type="button" key={m} onClick={() => set("duration_minutes")(m)} data-testid={`task-duration-preset-${m}`}
                  className={`rounded-full px-3 h-11 text-xs font-semibold border transition-colors duration-200 ${Number(form.duration_minutes) === m ? "bg-[#103080] text-white border-[#103080]" : "border-[#103080]/20 text-[#103080] hover:border-[#0090D0]"}`}>
                  {m < 60 ? `${m}m` : `${m / 60}h`}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <PickSelect value={form.priority} onChange={set("priority")} options={PRIORITIES} testId="task-priority-select" />
            </div>
            <div className="space-y-1.5">
              <Label>Deadline</Label>
              <Input type="date" value={form.deadline} onChange={set("deadline")} className={field} data-testid="task-deadline-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Don't start before (optional)</Label>
            <Input type="date" value={form.start_date} onChange={set("start_date")} className={field} data-testid="task-start-date-input" />
          </div>
          {task?.job_id && (
            <p className="text-xs text-[#0090D0] font-medium" data-testid="task-recurring-note">From the {task.period_label || ""} job card for {task.client_name}.</p>
          )}
          {task?.pinned?.length > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-[#0090D0]/[0.07] border border-[#0090D0]/25 px-3 py-2 text-sm text-[#103080]" data-testid="task-pinned-note">
              <span className="inline-flex items-center gap-1.5"><Pin size={13} /> Pinned to {task.pinned.length} time slot{task.pinned.length > 1 ? "s" : ""}</span>
              <button type="button" onClick={unpin} data-testid="task-unpin-button" className="text-xs font-semibold text-[#0090D0] hover:text-[#103080] transition-colors duration-200">Unpin</button>
            </div>
          )}
          {task?.subtasks?.length > 0 && <Subtasks task={task} />}
          {team.length > 0 && (
            <div className="space-y-1.5">
              <Label>Assigned to</Label>
              <PickSelect value={form.user_id || team[0].id} onChange={set("user_id")} testId="task-assignee-select"
                options={team.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} className="rounded-xl bg-[#FDFBF7] border-[#103080]/20" data-testid="task-notes-input" />
          </div>
          <div className="flex items-center justify-between pt-2">
            {editing ? (
              <button type="button" onClick={remove} data-testid="task-delete-button"
                className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors duration-200">
                <Trash2 size={15} /> Delete
              </button>
            ) : <span />}
            <button type="submit" disabled={busy} data-testid="task-save-button"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200 disabled:opacity-60">
              {editing ? "Save changes" : "Add & schedule"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
